import React, {useMemo} from 'react';

const LINKIFY_MATCHER =
  /(\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b)|(\bhttps?:\/\/[^\s<]+)|(\bwww\.[^\s<]+)|(\b(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)/gi;

const NEW_TAB_REL_VALUES = ['noopener', 'noreferrer'];

const stripTrailingPunctuation = value => {
  const trailingPunctuation = '.,;:!?)]}\'"';
  let url = value;
  let trailing = '';

  while (url && trailingPunctuation.includes(url.slice(-1))) {
    trailing = url.slice(-1) + trailing;
    url = url.slice(0, -1);
  }

  return {url, trailing};
};

const normalizeHref = candidate => {
  if (/^https?:\/\//i.test(candidate)) return candidate;
  if (/^www\./i.test(candidate)) return `https://${candidate}`;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(candidate)) return `https://${candidate}`;
  return candidate;
};

const setLinkToOpenInNewTab = link => {
  const relValues = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
  NEW_TAB_REL_VALUES.forEach(value => relValues.add(value));

  link.setAttribute('target', '_blank');
  link.setAttribute('rel', Array.from(relValues).join(' '));
};

const prepareRichTextHtml = html => {
  const sourceHtml = String(html || '');

  if (!sourceHtml || typeof window === 'undefined' || !window.DOMParser) return sourceHtml;

  const document = new window.DOMParser().parseFromString(sourceHtml, 'text/html');
  const textNodeType = window.NodeFilter?.SHOW_TEXT || 4;
  const walker = document.createTreeWalker(document.body, textNodeType);
  const textNodes = [];

  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach(node => {
    const parentTag = node.parentNode?.nodeName?.toLowerCase() || '';
    if (!node.nodeValue?.trim() || ['a', 'script', 'style'].includes(parentTag)) return;

    const text = node.nodeValue;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    LINKIFY_MATCHER.lastIndex = 0;
    while ((match = LINKIFY_MATCHER.exec(text)) !== null) {
      const matchedText = match[0];

      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const isEmail = Boolean(match[1]);
      const {url, trailing} = isEmail
        ? {url: matchedText, trailing: ''}
        : stripTrailingPunctuation(matchedText);
      const link = document.createElement('a');

      link.setAttribute('href', isEmail ? `mailto:${url}` : normalizeHref(url));
      link.textContent = url;
      fragment.appendChild(link);

      if (trailing) fragment.appendChild(document.createTextNode(trailing));

      lastIndex = match.index + matchedText.length;
    }

    if (lastIndex === 0) return;
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode.replaceChild(fragment, node);
  });

  document.querySelectorAll('a[href]').forEach(link => {
    const href = link.getAttribute('href') || '';
    link.setAttribute('href', normalizeHref(href));
    setLinkToOpenInNewTab(link);
  });

  return document.body.innerHTML;
};

const RichTextContent = ({as: Component = 'div', html = '', ...props}) => {
  const preparedHtml = useMemo(() => prepareRichTextHtml(html), [html]);

  return <Component {...props} dangerouslySetInnerHTML={{__html: preparedHtml}} />;
};

export default RichTextContent;
